import { useEffect, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'

import Toast from 'components/atoms/Toast'
import { useCorrectDeviceAccessorySerialMutation } from 'store/services/devices'
import type { DeviceAccessory } from 'store/types'

interface IProps {
  deviceId: number
  // the accessory whose serial to correct; the modal is closed while null
  accessory: (DeviceAccessory & { label: string }) | null
  onClose: () => void
}

// TES-3191: puts a mistyped accessory serial right. Unlike Replace, the accessory keeps its count.
export const AccessorySerialModal = ({ deviceId, accessory, onClose }: IProps) => {
  const [serial, setSerial] = useState('')
  const [correctSerial, { isLoading }] = useCorrectDeviceAccessorySerialMutation()

  useEffect(() => {
    setSerial(accessory?.serial ?? '')
  }, [accessory])

  if (!accessory) return null

  const name = accessory.label.toLowerCase()
  const unchanged = serial.trim() === (accessory.serial ?? '')

  const save = async () => {
    try {
      await correctSerial({ id: deviceId, accessoryId: accessory.id, serial: serial.trim() || null }).unwrap()
      Toast({ type: 'success', label: `${accessory.label} serial updated` })
      onClose()
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || `Failed to update the ${name} serial` })
    }
  }

  return (
    <Modal centered show onHide={onClose}>
      <Modal.Header>
        <Modal.Title>Edit {accessory.label} Serial</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">
          Corrects a mistyped serial. The {name} keeps its {accessory.uses}{' '}
          {accessory.uses === 1 ? 'use' : 'uses'}; to put on a new one, use Replace.
        </p>
        <label htmlFor="accessorySerialCorrection" className="textGray">
          {accessory.label} serial
        </label>
        <input
          id="accessorySerialCorrection"
          className="form-control"
          value={serial}
          maxLength={150}
          autoFocus
          onChange={(event) => setSerial(event.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={save} disabled={unchanged || isLoading}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
